// Transport-agnostic contract. The UI, hook and dispatcher depend only on this — never on an SDK.

import {
  AssistantRealtimeState,
  AssistantSpeakerRole,
  type AssistantToolName
} from '../../../../../../pure-js/types/AppAssistant'

export { AssistantRealtimeState }

export type AssistantTranscriptEvent = {
  role: AssistantSpeakerRole
  /** Incremental text for the current turn (append on the consumer side). */
  text: string
}

export type AssistantToolCall = {
  /** Provider-assigned call id; echoed back in the response. */
  id?: string
  name: AssistantToolName | string
  args: Record<string, unknown>
}

/** The tool completed, but its empty acknowledgement must not trigger another model utterance. */
export const SILENT_ASSISTANT_TOOL_RESPONSE = Symbol('silent-assistant-tool-response')

export type AssistantToolResult = Record<string, unknown> | typeof SILENT_ASSISTANT_TOOL_RESPONSE

export type AssistantRealtimeCallbacks = {
  onStateChange?: (state: AssistantRealtimeState) => void
  onUserTranscript?: (event: AssistantTranscriptEvent) => void
  onAssistantTranscript?: (event: AssistantTranscriptEvent) => void
  /** Model audio output: 16-bit PCM @ 24 kHz mono. */
  onAudio?: (pcm: Int16Array) => void
  /** Resolve a tool call against the client whitelist; terminal calls return a silent acknowledgement. */
  onToolCall?: (call: AssistantToolCall) => Promise<AssistantToolResult> | AssistantToolResult
  /** Barge-in: the model's output was interrupted — stop playback and clear the queue. */
  onInterrupted?: () => void
  onTurnComplete?: () => void
  onError?: (error: Error) => void
}

export interface AssistantRealtimeTransport {
  readonly state: AssistantRealtimeState
  connect(): Promise<void>
  /** Send a typed user message. */
  sendText(text: string): void
  /** Send a mic audio chunk: 16-bit PCM @ 16 kHz mono. */
  sendAudioChunk(pcm: Int16Array): void
  close(): void
}
